import type { EventCalendarRangeDates } from '@repo/hooks/useEventCalendarRangeDates';
import type { MemberChanges } from '@repo/types/memberChanges';
import type { Dispatch, SetStateAction } from 'react';

import { useEditMeeting } from '@repo/api/meeting/useEditMeeting';
import { useGetMeeting } from '@repo/api/meeting/useGetMeeting';
import { calendarUpsertSchema, type CalendarUpsertSchema } from '@repo/schemas/meeting/calendarUpsert';
import { LoaderCircle } from 'lucide-react';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { MeetingFormContent } from '@/components/features/calendar/form/meeting-form-content/MeetingFormContent';
import { Dialog, DialogContent } from '@/components/ui/dialog';

interface EditMeetingProps {
  id?: number;
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  queryParams?: EventCalendarRangeDates;
}

export default function EditMeeting(editMeetingProps: EditMeetingProps) {
  const { id, open, onOpenChange, queryParams } = editMeetingProps;
  const { mutateAsync: updateMeeting, isPending: isUpdating } = useEditMeeting(queryParams);

  const { data: meetingData, isFetching } = useGetMeeting(id, open);

  const formData = useMemo<CalendarUpsertSchema | undefined>(() => calendarUpsertSchema.safeParse(meetingData).data, [meetingData]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const calculateMemberChanges = (userIds: number[], groupIds: number[]): Array<MemberChanges> => {
    const existingUserIds = (meetingData?.users || []).map((user) => user.id);
    const existingGroupIds = (meetingData?.groups || []).map((group) => group.id);

    const usersToAdd = userIds.filter((userId) => !existingUserIds.includes(userId));
    const usersToRemove = existingUserIds.filter((userId) => !userIds.includes(userId));

    const groupsToAdd = groupIds.filter((groupId) => !existingGroupIds.includes(groupId));
    const groupsToRemove = existingGroupIds.filter((groupId) => !groupIds.includes(groupId));

    return [
      {
        userIds: usersToAdd,
        groupIds: groupsToAdd,
        add: true,
        remove: false,
      },
      {
        userIds: usersToRemove,
        groupIds: groupsToRemove,
        add: false,
        remove: true,
      },
    ];
  };

  const handleSubmit = async (data: CalendarUpsertSchema) => {
    if (!id) {
      toast.error('Nie można edytować spotkania bez ID.');
      return;
    }

    const { users, groups, location, ...meeting } = data;
    const userArr = users.map((user) => user.id);
    const groupArr = groups.map((group) => group.id);
    const locationId = location?.id ?? null;

    const memberChanges = calculateMemberChanges(userArr, groupArr);

    await updateMeeting({
      ...meeting,
      id,
      locationId,
      memberChanges,
    })
      .then(() => {
        handleClose();
      })
      .catch(() => {
        toast.error('Nie udało się zaktualizować spotkania.');
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'max-h-200 sm:h-[90dvh] sm:min-h-96 sm:max-w-120'} onCloseAutoFocus={handleClose} onEscapeKeyDown={handleClose} isClosable={false}>
        {isUpdating && (
          <div className={'bg-background/75 absolute inset-0 z-60 m-auto flex flex-wrap items-center justify-center gap-3 backdrop-blur-md'} role="status" aria-live="polite">
            <LoaderCircle className={'animate-spin'} aria-hidden={'true'} />
            <h5 className={'font-medium'}>Trwa zapisywanie zmian...</h5>
          </div>
        )}
        {isFetching && (
          <div className={'bg-background/75 absolute inset-0 z-60 m-auto flex flex-wrap items-center justify-center gap-3 backdrop-blur-md'} role="status" aria-live="polite">
            <LoaderCircle className={'animate-spin'} aria-hidden={'true'} />
            <h5 className={'font-medium'}>Trwa ładowanie danych...</h5>
          </div>
        )}
        <MeetingFormContent callback={handleSubmit} open={open} onOpenChange={onOpenChange} formData={formData} />
      </DialogContent>
    </Dialog>
  );
}
